import { useState, useEffect, useRef, useCallback } from "react";

const DEFAULT_INTERVAL = 900000; // 15 minutos

/**
 * Hook para polling periódico de datos.
 * Ejecuta fetchFn inmediatamente y luego cada intervalMs milisegundos.
 * Pausa cuando el tab no está visible (Page Visibility API).
 * Reanuda y ejecuta fetch inmediato al volver al tab.
 *
 * @param {Function} fetchFn - Función async a ejecutar periódicamente
 * @param {number} [intervalMs=900000] - Intervalo en ms (default 15 min)
 * @param {boolean} [enabled=true] - Si el polling está habilitado
 * @returns {{ lastUpdated: string|null, isStale: boolean, refetch: Function }}
 */
const usePolling = (fetchFn, intervalMs = DEFAULT_INTERVAL, enabled = true) => {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const intervalRef = useRef(null);
  const fetchFnRef = useRef(fetchFn);

  // Mantener referencia actualizada de fetchFn para evitar recrear el intervalo
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  }, [fetchFn]);

  const startInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(async () => {
      try {
        await fetchFnRef.current();
        setLastUpdated(new Date().toISOString());
        setIsStale(false);
      } catch {
        setIsStale(true);
      }
    }, intervalMs);
  }, [intervalMs]);

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const refetch = useCallback(async () => {
    try {
      await fetchFnRef.current();
      setLastUpdated(new Date().toISOString());
      setIsStale(false);
    } catch {
      setIsStale(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      stopInterval();
      return;
    }

    // Ejecutar fetch inmediato al montar
    refetch();
    // Iniciar intervalo
    startInterval();

    // Page Visibility API: pausar/reanudar polling
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopInterval();
      } else {
        // Tab vuelve a ser visible: ejecutar fetch inmediato y reiniciar intervalo
        refetch();
        startInterval();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup al desmontar
    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, startInterval, stopInterval, refetch]);

  return { lastUpdated, isStale, refetch };
};

export default usePolling;
